import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../../../config/config';
import Loader from '../../../components/Loader';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const NotificacionesManager = () => {
    // Estados para el formulario de creación
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [enviarATodos, setEnviarATodos] = useState(true);
    const [enviarAAdmins, setEnviarAAdmins] = useState(false);
    const [enviarATecnicos, setEnviarATecnicos] = useState(false);
    const [enviarAAdminsYTecnicos, setEnviarAAdminsYTecnicos] = useState(false);
    const [usuarioSeleccionado, setUsuarioSeleccionado] = useState('');
    const [busquedaUsuario, setBusquedaUsuario] = useState('');
    const [mostrarListaUsuarios, setMostrarListaUsuarios] = useState(false);
    const [tipo, setTipo] = useState('sistema');
    const [titulo, setTitulo] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [link, setLink] = useState('');
    const [icono, setIcono] = useState('campana');
    const [prioridad, setPrioridad] = useState('normal');

    // Estados para la gestión
    const [cargando, setCargando] = useState(true);
    const [notificaciones, setNotificaciones] = useState([]);
    const [notificacionesFiltradas, setNotificacionesFiltradas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [estadisticas, setEstadisticas] = useState(null);
    
    // Filtros
    const [filtroTipo, setFiltroTipo] = useState('todas');
    const [filtroEstado, setFiltroEstado] = useState('todas');
    const [busqueda, setBusqueda] = useState('');
    
    // Paginación
    const [paginaActual, setPaginaActual] = useState(1);
    const elementosPorPagina = 15;

    const token = localStorage.getItem('AuthToken');
    const tokenInfo = token ? jwtDecode(token) : {};
    const navigate = useNavigate();

    // Cargar datos iniciales
    useEffect(() => {
        if (tokenInfo.role !== 'admin') {
            navigate('/');
            return;
        }
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            const peticionNotificaciones = axios.get(`${BASE_URL}/notificaciones-admin/todas?limite=200`, {
                headers: { Authorization: token }
            });
            const peticionUsuarios = axios.get(`${BASE_URL}/notificaciones-admin/usuarios`, {
                headers: { Authorization: token }
            });
            const peticionEstadisticas = axios.get(`${BASE_URL}/notificaciones-admin/estadisticas`, {
                headers: { Authorization: token }
            });
            const esperaMinima = new Promise(resolve => setTimeout(resolve, 800));

            const [respNotif, respUsers, respStats] = await Promise.all([
                peticionNotificaciones,
                peticionUsuarios,
                peticionEstadisticas,
                esperaMinima
            ]);

            if (respNotif.data.success) {
                const ordenadas = respNotif.data.notificaciones.sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );
                setNotificaciones(ordenadas);
                setNotificacionesFiltradas(ordenadas);
            }

            if (respUsers.data.success) {
                setUsuarios(respUsers.data.usuarios);
            }

            if (respStats.data.success) {
                setEstadisticas(respStats.data.estadisticas);
            }
        } catch (error) {
            console.error('Error al cargar datos:', error);
            toast.error('Error al cargar datos');
        } finally {
            setCargando(false);
        }
    };

    // Aplicar filtros
    useEffect(() => {
        let resultado = [...notificaciones];

        // Filtro por tipo
        if (filtroTipo !== 'todas') {
            resultado = resultado.filter(n => n.tipo === filtroTipo);
        }

        // Filtro por estado
        if (filtroEstado === 'individuales') {
            resultado = resultado.filter(n => n.usuario_destinatario !== null);
        } else if (filtroEstado === 'masivas') {
            resultado = resultado.filter(n => n.usuario_destinatario === null);
        } else if (filtroEstado === 'leidas') {
            resultado = resultado.filter(n => n.usuario_destinatario && n.leida === true);
        } else if (filtroEstado === 'no_leidas') {
            resultado = resultado.filter(n => n.usuario_destinatario && n.leida === false);
        }

        // Búsqueda por texto
        if (busqueda.trim()) {
            resultado = resultado.filter(n => 
                n.titulo?.toLowerCase().includes(busqueda.toLowerCase()) ||
                n.mensaje?.toLowerCase().includes(busqueda.toLowerCase()) ||
                n.usuario_destinatario?.toLowerCase().includes(busqueda.toLowerCase())
            );
        }

        setNotificacionesFiltradas(resultado);
        setPaginaActual(1);
    }, [filtroTipo, filtroEstado, busqueda, notificaciones]);

    // Enviar notificación
    const enviarNotificacion = async (e) => {
        e.preventDefault();

        if (!titulo.trim() || !mensaje.trim()) {
            toast.error('El título y mensaje son obligatorios');
            return;
        }

        if (!enviarATodos && !enviarAAdmins && !enviarATecnicos && !enviarAAdminsYTecnicos && !usuarioSeleccionado) {
            toast.error('Selecciona un usuario destinatario');
            return;
        }

        try {
            const datos = {
                tipo,
                titulo: titulo.trim(),
                mensaje: mensaje.trim(),
                link: link.trim() || null,
                icono,
                prioridad,
                enviar_a_todos: enviarATodos,
                enviar_a_admins: enviarAAdmins || enviarAAdminsYTecnicos,
                enviar_a_tecnicos: enviarATecnicos || enviarAAdminsYTecnicos,
                usuario_destinatario: (enviarATodos || enviarAAdmins || enviarATecnicos || enviarAAdminsYTecnicos) ? null : usuarioSeleccionado
            };

            const response = await axios.post(`${BASE_URL}/notificaciones`, datos, {
                headers: { Authorization: token }
            });

            if (response.data.success) {
                toast.success(response.data.message);
                limpiarFormulario();
                setMostrarFormulario(false);
                cargarDatos();
            }
        } catch (error) {
            console.error('Error al enviar notificación:', error);
            toast.error('Error al enviar notificación');
        }
    };

    const limpiarFormulario = () => {
        setTitulo('');
        setMensaje('');
        setLink('');
        setTipo('sistema');
        setIcono('campana');
        setPrioridad('normal');
        setUsuarioSeleccionado('');
        setBusquedaUsuario('');
        setEnviarATodos(true);
        setEnviarAAdmins(false);
        setEnviarATecnicos(false);
        setEnviarAAdminsYTecnicos(false);
    };

    // Filtrar usuarios por búsqueda
    const usuariosFiltrados = usuarios.filter(u => {
        const textoCompleto = `${u.nombre} ${u.apellidos} ${u.usuario} ${u.email}`.toLowerCase();
        return textoCompleto.includes(busquedaUsuario.toLowerCase());
    });

    const seleccionarUsuario = (usuario) => {
        setUsuarioSeleccionado(usuario.usuario);
        setBusquedaUsuario(`${usuario.nombre} ${usuario.apellidos} (@${usuario.usuario})`);
        setMostrarListaUsuarios(false);
    };

    const limpiarSeleccionUsuario = () => {
        setUsuarioSeleccionado('');
        setBusquedaUsuario('');
    };

    const eliminarNotificacion = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar esta notificación?')) return;

        try {
            const response = await axios.delete(`${BASE_URL}/notificaciones/${id}`, {
                headers: { Authorization: token }
            });

            if (response.data.success) {
                toast.success('Notificación eliminada');
                cargarDatos();
            }
        } catch (error) {
            console.error('Error al eliminar:', error);
            toast.error('Error al eliminar notificación');
        }
    };

    const limpiarNotificacionesAntiguas = async () => {
        if (!window.confirm('¿Eliminar notificaciones leídas de más de 30 días?')) return;

        try {
            const response = await axios.delete(`${BASE_URL}/notificaciones-admin/limpiar-antiguas?dias=30`, {
                headers: { Authorization: token }
            });

            if (response.data.success) {
                toast.success(`${response.data.eliminadas} notificaciones eliminadas`);
                cargarDatos();
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error al limpiar notificaciones');
        }
    };

    // Paginación
    const totalPaginas = Math.ceil(notificacionesFiltradas.length / elementosPorPagina);
    const indiceUltimo = paginaActual * elementosPorPagina;
    const indicePrimero = indiceUltimo - elementosPorPagina;
    const notificacionesActuales = notificacionesFiltradas.slice(indicePrimero, indiceUltimo);

    const cambiarPagina = (numeroPagina) => {
        if (numeroPagina >= 1 && numeroPagina <= totalPaginas) {
            setPaginaActual(numeroPagina);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    if (cargando) return <Loader />;

    return (
        <main className="site-width">
            <ToastContainer position="bottom-right" autoClose={3000} />
            
            <div className="contenedor-gestor-notificaciones">
                <button className="boton-volver" onClick={() => navigate('/dashboard')}>
                    ← Volver
                </button>
                <div className="cabecera-gestor">
                    <div>
                        <h2 className="titulo-seccion">Gestor de Notificaciones</h2>
                        <p>Envía notificaciones personalizadas a usuarios específicos o a todos</p>
                    </div>
                    <button 
                        className="boton-primario"
                        onClick={() => setMostrarFormulario(!mostrarFormulario)}
                    >
                        {mostrarFormulario ? '✕ Cancelar' : '+ Nueva Notificación'}
                    </button>
                </div>

                {/* Estadísticas */}
                {estadisticas && (
                    <div className="tarjetas-estadisticas">
                        <div className="tarjeta-estadistica">
                            <h4>Total Notificaciones</h4>
                            <p className="numero-grande">{estadisticas.totalNotificaciones}</p>
                            <small>{estadisticas.notificacionesMasivas} masivas • {estadisticas.notificacionesIndividuales} individuales</small>
                        </div>
                        <div className="tarjeta-estadistica">
                            <h4>Individuales No Leídas</h4>
                            <p className="numero-grande destacado">{estadisticas.individualesNoLeidas}</p>
                            <small>Solo notificaciones individuales</small>
                        </div>
                        <div className="tarjeta-estadistica">
                            <h4>Individuales Leídas</h4>
                            <p className="numero-grande">{estadisticas.individualesLeidas}</p>
                            <small>{estadisticas.porcentajeLeidasIndividuales}% del total individual</small>
                        </div>
                        <div className="tarjeta-estadistica">
                            <h4>Usuarios Verificados</h4>
                            <p className="numero-grande">{estadisticas.totalUsuariosVerificados}</p>
                            <small>Alcance de notificaciones masivas</small>
                        </div>
                    </div>
                )}

                {/* Modal de formulario de creación */}
                {mostrarFormulario && (
                    <div className="modal-overlay-notificaciones" onClick={() => setMostrarFormulario(false)}>
                        <div className="modal-contenido-notificaciones" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header-notificaciones">
                                <h3>Nueva Notificación</h3>
                                <button 
                                    type="button"
                                    className="boton-cerrar-modal"
                                    onClick={() => setMostrarFormulario(false)}
                                >
                                    ✕
                                </button>
                            </div>
                            <form onSubmit={enviarNotificacion} className="formulario-modal-notificacion">
                            <div className="grid-formulario">
                                <div className="campo-formulario">
                                    <label>Destinatario *</label>
                                    <div className="grupo-radio">
                                                <label className="radio-opcion">
                                                    <input 
                                                        type="radio"
                                                        checked={enviarATodos}
                                                        onChange={() => {
                                                            setEnviarATodos(true);
                                                            setEnviarAAdmins(false);
                                                            setEnviarATecnicos(false);
                                                            setEnviarAAdminsYTecnicos(false);
                                                        }}
                                                    />
                                                    Todos los usuarios
                                                </label>
                                                <label className="radio-opcion">
                                                    <input 
                                                        type="radio"
                                                        checked={enviarAAdmins}
                                                        onChange={() => {
                                                            setEnviarATodos(false);
                                                            setEnviarAAdmins(true);
                                                            setEnviarATecnicos(false);
                                                            setEnviarAAdminsYTecnicos(false);
                                                            setBusquedaUsuario('');
                                                            setUsuarioSeleccionado('');
                                                        }}
                                                    />
                                                    Solo administradores
                                                </label>
                                                <label className="radio-opcion">
                                                    <input 
                                                        type="radio"
                                                        checked={enviarATecnicos}
                                                        onChange={() => {
                                                            setEnviarATodos(false);
                                                            setEnviarAAdmins(false);
                                                            setEnviarATecnicos(true);
                                                            setEnviarAAdminsYTecnicos(false);
                                                            setBusquedaUsuario('');
                                                            setUsuarioSeleccionado('');
                                                        }}
                                                    />
                                                    Solo técnicos
                                                </label>
                                                <label className="radio-opcion">
                                                    <input 
                                                        type="radio"
                                                        checked={enviarAAdminsYTecnicos}
                                                        onChange={() => {
                                                            setEnviarATodos(false);
                                                            setEnviarAAdmins(false);
                                                            setEnviarATecnicos(false);
                                                            setEnviarAAdminsYTecnicos(true);
                                                            setBusquedaUsuario('');
                                                            setUsuarioSeleccionado('');
                                                        }}
                                                    />
                                                    Administradores y técnicos
                                                </label>
                                                <label className="radio-opcion">
                                                    <input 
                                                        type="radio"
                                                        checked={!enviarATodos && !enviarAAdmins && !enviarATecnicos && !enviarAAdminsYTecnicos}
                                                        onChange={() => {
                                                            setEnviarATodos(false);
                                                            setEnviarAAdmins(false);
                                                            setEnviarATecnicos(false);
                                                            setEnviarAAdminsYTecnicos(false);
                                                            setBusquedaUsuario('');
                                                            setUsuarioSeleccionado('');
                                                        }}
                                                    />
                                                    Usuario específico
                                                </label>
                                    </div>
                                    {!enviarATodos && !enviarAAdmins && (
                                        <div className="selector-usuario-busqueda">
                                            <div className="campo-busqueda-usuario">
                                                <input
                                                    type="text"
                                                    value={busquedaUsuario}
                                                    onChange={(e) => {
                                                        setBusquedaUsuario(e.target.value);
                                                        setMostrarListaUsuarios(true);
                                                        if (!e.target.value) {
                                                            setUsuarioSeleccionado('');
                                                        }
                                                    }}
                                                    onFocus={() => setMostrarListaUsuarios(true)}
                                                    placeholder="Buscar usuario por nombre, apellidos o usuario..."
                                                    className="input-busqueda-usuario"
                                                />
                                                {usuarioSeleccionado && (
                                                    <button
                                                        type="button"
                                                        className="boton-limpiar-usuario"
                                                        onClick={limpiarSeleccionUsuario}
                                                        title="Limpiar selección"
                                                    >
                                                        ✕
                                                    </button>
                                                )}
                                            </div>
                                            {mostrarListaUsuarios && busquedaUsuario && (
                                                <div className="lista-usuarios-desplegable">
                                                    {usuariosFiltrados.length > 0 ? (
                                                        usuariosFiltrados.map(u => (
                                                            <div
                                                                key={u.usuario}
                                                                className="item-usuario-desplegable"
                                                                onClick={() => seleccionarUsuario(u)}
                                                            >
                                                                <div className="info-usuario-item">
                                                                    <span className="nombre-usuario-item">
                                                                        {u.nombre} {u.apellidos}
                                                                    </span>
                                                                    <span className="usuario-item">@{u.usuario}</span>
                                                                </div>
                                                                <span className="email-usuario-item">{u.email}</span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="sin-resultados-usuario">
                                                            No se encontraron usuarios
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="campo-formulario">
                                    <label>Tipo de notificación</label>
                                    <select 
                                        value={tipo}
                                        onChange={(e) => setTipo(e.target.value)}
                                        className="selector"
                                    >
                                        <option value="sistema">Sistema</option>
                                        <option value="ticket">Ticket</option>
                                        <option value="proyecto">Proyecto</option>
                                        <option value="usuario">Usuario</option>
                                    </select>
                                </div>

                                <div className="campo-formulario">
                                    <label>Icono</label>
                                    <select 
                                        value={icono}
                                        onChange={(e) => setIcono(e.target.value)}
                                        className="selector"
                                    >
                                        <option value="campana">🔔 Campana</option>
                                        <option value="ticket">🎫 Ticket</option>
                                        <option value="info">ℹ️ Información</option>
                                        <option value="alerta">⚠️ Alerta</option>
                                        <option value="usuario">👤 Usuario</option>
                                    </select>
                                </div>

                                <div className="campo-formulario">
                                    <label>Prioridad</label>
                                    <select 
                                        value={prioridad}
                                        onChange={(e) => setPrioridad(e.target.value)}
                                        className="selector"
                                    >
                                        <option value="baja">Baja</option>
                                        <option value="normal">Normal</option>
                                        <option value="alta">Alta</option>
                                        <option value="urgente">Urgente</option>
                                    </select>
                                </div>
                            </div>

                            <div className="campo-formulario">
                                <label>Título *</label>
                                <input 
                                    type="text"
                                    value={titulo}
                                    onChange={(e) => setTitulo(e.target.value)}
                                    placeholder="Título de la notificación"
                                    maxLength={255}
                                    required
                                />
                            </div>

                            <div className="campo-formulario">
                                <label>Mensaje *</label>
                                <textarea 
                                    value={mensaje}
                                    onChange={(e) => setMensaje(e.target.value)}
                                    placeholder="Contenido del mensaje"
                                    rows={4}
                                    required
                                />
                            </div>

                            <div className="campo-formulario">
                                <label>Enlace (opcional)</label>
                                <input 
                                    type="text"
                                    value={link}
                                    onChange={(e) => setLink(e.target.value)}
                                    placeholder="/ruta-interna o https://ejemplo.com"
                                />
                                <small>Ruta interna (ej: /ticket/123) o URL completa</small>
                            </div>

                                <div className="botones-formulario">
                                    <button type="submit" className="boton-primario">
                                        Enviar 
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Filtros */}
                <div className="filtros-notificaciones">
                    <input
                        type="text"
                        placeholder="Buscar por título, mensaje o usuario..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="campo-busqueda"
                    />
                    <select
                        value={filtroTipo}
                        onChange={(e) => setFiltroTipo(e.target.value)}
                        className="selector"
                    >
                        <option value="todas">Todos los tipos</option>
                        <option value="sistema">Sistema</option>
                        <option value="ticket">Ticket</option>
                        <option value="proyecto">Proyecto</option>
                        <option value="usuario">Usuario</option>
                    </select>
                    <select
                        value={filtroEstado}
                        onChange={(e) => setFiltroEstado(e.target.value)}
                        className="selector"
                    >
                        <option value="todas">Todos los estados</option>
                        <option value="individuales">Individuales</option>
                        <option value="masivas">Masivas</option>
                        <option value="no_leidas">Individuales no leídas</option>
                        <option value="leidas">Individuales leídas</option>
                    </select>
                    <button 
                        className="boton-secundario"
                        onClick={limpiarNotificacionesAntiguas}
                    >
                         Limpiar antiguas
                    </button>
                </div>

                {/* Lista de notificaciones */}
                <div className="lista-notificaciones">
                    <p className="info-resultados">
                        Mostrando {notificacionesActuales.length} de {notificacionesFiltradas.length} notificaciones
                    </p>
                    
                    {notificacionesActuales.length > 0 ? (
                        notificacionesActuales.map((notif) => (
                            <div key={notif.id} className={`tarjeta-notificacion-admin ${notif.leida ? 'leida' : 'no-leida'}`}>
                                <div className="contenido-tarjeta-notificacion">
                                    <div className="info-principal">
                                        <span className={`etiqueta-tipo tipo-${notif.tipo}`}>
                                            {notif.tipo}
                                        </span>
                                        <span className={`etiqueta-prioridad prioridad-${notif.prioridad}`}>
                                            {notif.prioridad}
                                        </span>
                                        {notif.estadisticas?.tipo === 'individual' && (
                                            <span className={`etiqueta-estado ${notif.leida ? 'estado-leida' : 'estado-no-leida'}`}>
                                                {notif.leida ? '✓ Leída' : '● No leída'}
                                            </span>
                                        )}
                                        {notif.estadisticas?.tipo === 'masiva' && (
                                            <span className="etiqueta-masiva">
                                                Masiva
                                            </span>
                                        )}
                                    </div>
                                    
                                    <h4>{notif.titulo}</h4>
                                    <p className="mensaje-notificacion">{notif.mensaje}</p>
                                    
                                    {/* Sección de estadísticas destacada */}
                                    {notif.estadisticas && (
                                        <div className="seccion-estadisticas-notif">
                                            {notif.estadisticas.tipo === 'masiva' ? (
                                                <>
                                                    <div className="stat-item">
                                                        <span className="stat-icono">👥</span>
                                                        <div className="stat-info">
                                                            <span className="stat-valor">{notif.estadisticas.destinatarios}</span>
                                                            <span className="stat-label">Usuarios alcanzados</span>
                                                        </div>
                                                    </div>
                                                    <div className="stat-item">
                                                        <span className="stat-icono">✓</span>
                                                        <div className="stat-info">
                                                            <span className="stat-valor">Verificados</span>
                                                            <span className="stat-label">Estado de usuarios</span>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="stat-item">
                                                        <span className="stat-icono">👤</span>
                                                        <div className="stat-info">
                                                            <span className="stat-valor">{notif.estadisticas.destinatario}</span>
                                                            <span className="stat-label">Destinatario único</span>
                                                        </div>
                                                    </div>
                                                    <div className="stat-item">
                                                        <span className="stat-icono">{notif.estadisticas.vista ? '✓' : '○'}</span>
                                                        <div className="stat-info">
                                                            <span className="stat-valor">{notif.estadisticas.vista ? 'Leída' : 'No leída'}</span>
                                                            <span className="stat-label">Estado de lectura</span>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    
                                    <div className="info-secundaria">
                                        {notif.link && <span><b>🔗 Enlace:</b> {notif.link}</span>}
                                        <span><b>👨‍💼 Enviado por:</b> {notif.enviado_por}</span>
                                        <span><b>📅 Fecha:</b> {new Intl.DateTimeFormat('es-ES', {
                                            day: '2-digit',
                                            month: 'long',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        }).format(new Date(notif.createdAt))}</span>
                                    </div>
                                </div>
                                
                                <button 
                                    className="boton-eliminar"
                                    onClick={() => eliminarNotificacion(notif.id)}
                                    title="Eliminar notificación"
                                >
                                    🗑️
                                </button>
                            </div>
                        ))
                    ) : (
                        <p className="sin-resultados">No se encontraron notificaciones.</p>
                    )}
                </div>

                {/* Paginación */}
                {totalPaginas > 1 && (
                    <div className="paginacion">
                        <button 
                            onClick={() => cambiarPagina(paginaActual - 1)}
                            disabled={paginaActual === 1}
                            className="boton-paginacion"
                        >
                            ← Anterior
                        </button>
                        <span className="info-pagina">
                            Página {paginaActual} de {totalPaginas}
                        </span>
                        <button 
                            onClick={() => cambiarPagina(paginaActual + 1)}
                            disabled={paginaActual === totalPaginas}
                            className="boton-paginacion"
                        >
                            Siguiente →
                        </button>
                    </div>
                )}
            </div>
        </main>
    );
};

export default NotificacionesManager;
